import { BackButton } from "@/components/ui/back-button";
import { useState } from "react";
import { Settings2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Reports4Filters } from "@/components/reports/Reports4Filters";
import { Reports4KPIConfigPopup } from "@/components/reports/Reports4KPIConfigPopup";
import { Reports4KPIGrid } from "@/components/reports/Reports4KPIGrid";
import { WorksComparisonPanel } from "@/components/reports3/WorksComparisonPanel";
import { AIInsightsPanel } from "@/components/reports3/AIInsightsPanel";

export interface FilterState {
  obras: string[];
  responsaveis: string[];
  periodo: {
    tipo: 'hoje' | 'estaSemana' | 'esteMes' | 'esteAno' | 'ultimos30' | 'ultimos12meses' | 'todoPeriodo' | 'personalizado';
    inicio?: Date;
    fim?: Date;
  };
}

export interface Reports4KPIConfig {
  pontualidadeRDO: boolean;
  cumprimentoPrazos: boolean;
  tarefasAtrasadas: boolean;
  tarefasStatus: boolean;
  progressoFisico: boolean;
  equipamentos: boolean;
  histogramaPessoas: boolean;
}

export interface KPIConfig {
  pontualidadeRDO: boolean;
  tempoMedioPreenchimento: boolean;
  cumprimentoPrazos: boolean;
  tarefasAtrasadas: boolean;
  tarefasStatus: boolean;
  volumeRegistros: boolean;
  adocaoIA: boolean;
  progressoFisico: boolean;
}

const Reports = () => {
  const [filters, setFilters] = useState<FilterState>({
    obras: [],
    responsaveis: [],
    periodo: {
      tipo: 'ultimos30'
    }
  });

  const [kpiConfig, setKpiConfig] = useState<Reports4KPIConfig>({
    pontualidadeRDO: true,
    cumprimentoPrazos: true,
    tarefasAtrasadas: true,
    tarefasStatus: true,
    progressoFisico: true,
    equipamentos: true,
    histogramaPessoas: true,
  });

  const [configPopupOpen, setConfigPopupOpen] = useState(false);
  const [worksMinimized, setWorksMinimized] = useState(false);
  const [aiMinimized, setAiMinimized] = useState(false);
  const [worksHidden, setWorksHidden] = useState(false);
  const [aiHidden, setAiHidden] = useState(false);

  const activeKPIs = Object.values(kpiConfig).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="px-6 pt-4">
        <BackButton />
      </div>
      
      {/* Executive Header - Conforme Relatórios 3 */}
      <div className="sticky top-0 z-50 border-b bg-card/80 backdrop-blur-md supports-[backdrop-filter]:bg-card/80">
        <div className="container mx-auto px-4 sm:px-6 py-1">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-4xl font-bold tracking-tight bg-gradient-to-r from-foreground via-primary to-foreground/80 bg-clip-text text-transparent">
                Relatórios Executivos
              </h1>
              <p className="text-muted-foreground mt-2 text-sm sm:text-lg">
                Análise estratégica e inteligência de dados para tomada de decisão
              </p>
            </div>
            <Button
              variant="outline"
              size="lg"
              onClick={() => setConfigPopupOpen(true)}
              className="gap-2 bg-background/60 backdrop-blur border-primary/20 hover:bg-primary/5"
            >
              <Settings2 className="h-5 w-5" />
              Configurar Indicadores
              {activeKPIs > 0 && (
                <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
                  {activeKPIs}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Filters Bar - Baseado no Relatórios 2 com modificações */}
      <div className="sticky top-[80px] z-40 bg-card/70 border-b backdrop-blur-md">
        <div className="container mx-auto px-6 py-2">
          <Reports4Filters filters={filters} onFiltersChange={setFilters} />
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8 space-y-8">
        {activeKPIs > 0 ? (
          <>
            {/* KPI Grid */}
            <Reports4KPIGrid config={kpiConfig} filters={filters} />
          </>
        ) : (
          <Card className="text-center py-24 bg-card/50 backdrop-blur border-dashed border-2">
            <div className="max-w-lg mx-auto">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                <Settings2 className="h-10 w-10 text-primary" />
              </div>
              <h3 className="text-2xl font-semibold mb-3">Configure seus Indicadores</h3>
              <p className="text-muted-foreground mb-8 text-lg">
                Selecione os KPIs que deseja visualizar no seu dashboard executivo para uma análise estratégica completa
              </p>
              <Button 
                onClick={() => setConfigPopupOpen(true)}
                size="lg"
                className="gap-3"
              >
                <Settings2 className="h-5 w-5" />
                Configurar Indicadores
              </Button>
            </div>
          </Card>
        )}
        
        {/* Painéis Fixos na Parte Inferior */}
        <div className="space-y-8 mt-12">
          {/* Painel de obras - Conforme Relatórios 3 */}
          {!worksHidden && (
            <WorksComparisonPanel 
              filters={{
                obras: filters.obras,
                responsaveis: filters.responsaveis,
                periodo: {
                  tipo: 'ultimos30',
                  inicio: filters.periodo.inicio,
                  fim: filters.periodo.fim,
                }
              }}
              isMinimized={worksMinimized}
              onMinimize={() => setWorksMinimized(!worksMinimized)}
              onClose={() => setWorksHidden(true)}
            />
          )}
          
          {/* Inteligência com IA - Conforme Relatórios 3 */}
          {!aiHidden && (
            <AIInsightsPanel 
              filters={{
                obras: filters.obras,
                responsaveis: filters.responsaveis,
                periodo: {
                  tipo: 'ultimos30',
                  inicio: filters.periodo.inicio,
                  fim: filters.periodo.fim,
                }
              }}
              isMinimized={aiMinimized}
              onMinimize={() => setAiMinimized(!aiMinimized)}
              onClose={() => setAiHidden(true)}
            />
          )}
        </div>
      </div>

      <Reports4KPIConfigPopup
        open={configPopupOpen}
        onOpenChange={setConfigPopupOpen}
        config={kpiConfig}
        onConfigChange={setKpiConfig}
      />
    </div>
  );
};

export default Reports;